/**
 * E07-F12 — REAL adapter for the Jev admin block (Settings → Jev).
 *
 * Replaces the prototype mock (`../mock/jev.ts`) hook-for-hook; the components
 * did not change their imports (`../jevApi.ts` re-exports this module). Every
 * endpoint is admin-only on the server (routes/admin-jev.ts → requireRole('admin')).
 *
 *   GET  /admin/jev/settings                       → useJevSettings()            T01
 *   PUT  /admin/jev/settings                       → useUpdateJevSettings()      T01
 *   GET  /admin/jev/question-sets                  → useJevQuestionSets()        T02
 *   PUT  /admin/jev/question-sets/:setKey          → useSaveJevQuestionSet()     T02
 *        400 JEV_SET_INVALID { issues } → an Error naming every issue in plain words
 *   GET  /admin/jev/question-sets (+ per set …/thresholds, …/recalibration/latest)
 *                                                  → useJevThresholds()          T03
 *   POST …/:setKey/thresholds/rollback { versionId } → useRollbackJevThresholds() T03
 *   PUT  …/:setKey/thresholds/freeze { frozen }    → useSetJevFreeze()           T03
 *   GET  …/:setKey/recalibration/preview           → useJevRecalibrationQuote()  T03
 *   POST …/:setKey/recalibration { confirmedRows } → useStartJevRecalibration()  T03 (paid)
 *   GET  /admin/jev/metrics?days=30                → useJevMetrics()             T04
 *   GET  /admin/jev/check-rows                     → useJevCheckRows()           T04
 *   GET  …/:setKey/check-quote                     → useJevCheckQuote()          T04
 *   POST …/:setKey/check { rowId }                 → useRunJevSetCheck()         T04 (paid, not written)
 *
 * The server speaks the `@eer/shared` contract (contracts/jev.ts); this module maps
 * it to the view model in `../jevModel.ts` with PURE functions (exported for unit
 * tests). Writes are never retried (one click = one write; paid calls doubly so).
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { z } from 'zod';
import {
  jevCheckQuoteSchema,
  jevCheckResultSchema,
  jevCheckRowsResponseSchema,
  jevMetricsResponseSchema,
  jevQuestionSetListResponseSchema,
  jevQuestionSetSchema,
  jevRecalibrationLatestResponseSchema,
  jevRecalibrationPreviewSchema,
  jevSettingsResponseSchema,
  jevThresholdVersionSchema,
  type JevCheckResultApi,
  type JevCheckRowApi,
  type JevMetricsResponse,
  type JevQuestion as ApiJevQuestion,
  type JevQuestionSet as ApiJevQuestionSet,
  type JevRecalibrationPreview,
  type JevRecalibrationRun,
  type JevSetIssue,
  type JevSettingsResponse,
  type JevSettingsUpdate as ApiJevSettingsUpdate,
  type JevThresholdVersion as ApiJevThresholdVersion,
} from '@eer/shared';
import { apiClient, errorBodyOf, unwrap } from '../../api/client.js';
import {
  issueToLintError,
  questionSetToApi,
  type JevAnswerKind,
  type JevBand,
  type JevCheckQuote,
  type JevCheckResult,
  type JevCheckRow,
  type JevCriterion,
  type JevMetrics,
  type JevQuestion,
  type JevQuestionSet,
  type JevQuestionSetBody,
  type JevRecalibrationQuote,
  type JevSafeguardWarning,
  type JevSetKey,
  type JevSetThresholds,
  type JevSettings,
  type JevSettingsUpdate,
  type JevThresholdVersion,
  type JevVersionSource,
} from '../jevModel.js';

/** The command label the API logs these requests under (sibling parity). */
const COMMAND = 'manage_jev';

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const JEV_QUERY_KEY = ['admin', 'jev'] as const;
const SETTINGS_KEY = [...JEV_QUERY_KEY, 'settings'] as const;

function useInvalidateJev(): () => Promise<void> {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: JEV_QUERY_KEY });
}

// ---------------------------------------------------------------------------
// T01 — key, switch, verdict path: mapping (pure)
// ---------------------------------------------------------------------------

/** Server settings → view model. The key itself never crosses the wire. */
export function toUiSettings(r: JevSettingsResponse): JevSettings {
  return {
    hasKey: r.hasKey,
    maskedKey: r.maskedKey,
    enabled: r.enabled,
    verdictPath: r.verdictEngine,
    updatedAt: r.updatedAt ?? '',
    updatedBy: r.updatedByName ?? r.updatedBy ?? '—',
  };
}

/** View-model save → the PUT body. Key tri-state: keep = omit, clear = '', set = the value. */
export function toSettingsBody(u: JevSettingsUpdate): ApiJevSettingsUpdate {
  const body: ApiJevSettingsUpdate = { enabled: u.enabled, verdictEngine: u.verdictPath };
  if (u.key.kind === 'set') body.apiKey = u.key.value.trim();
  else if (u.key.kind === 'clear') body.apiKey = '';
  return body;
}

// ---------------------------------------------------------------------------
// T01 — transport + hooks
// ---------------------------------------------------------------------------

async function fetchSettings(): Promise<JevSettings> {
  const result = await apiClient.get<unknown>('/admin/jev/settings', { command: COMMAND });
  return toUiSettings(jevSettingsResponseSchema.parse(unwrap(result)));
}

async function putSettings(u: JevSettingsUpdate): Promise<JevSettings> {
  const result = await apiClient.put<unknown>('/admin/jev/settings', toSettingsBody(u), {
    command: COMMAND,
  });
  return toUiSettings(jevSettingsResponseSchema.parse(unwrap(result)));
}

export function useJevSettings(): UseQueryResult<JevSettings, Error> {
  return useQuery<JevSettings, Error>({ queryKey: SETTINGS_KEY, queryFn: fetchSettings });
}

export function useUpdateJevSettings(): UseMutationResult<JevSettings, Error, JevSettingsUpdate> {
  const invalidate = useInvalidateJev();
  return useMutation<JevSettings, Error, JevSettingsUpdate>({
    mutationFn: putSettings,
    retry: false,
    onSuccess: () => void invalidate(),
  });
}

// ---------------------------------------------------------------------------
// T02 — question sets: mapping (pure)
// ---------------------------------------------------------------------------

const SETS_KEY = [...JEV_QUERY_KEY, 'question-sets'] as const;

/** Wire question → view question. yes/no criteria `{true,false}` show as `yes` / `no`. */
export function toUiQuestion(name: string, q: ApiJevQuestion): JevQuestion {
  const kind: JevAnswerKind = q.type;
  let criteria: JevCriterion[];
  if (Array.isArray(q.criteria)) {
    criteria = q.criteria.map((anchor, i) => ({ key: String(i), anchor }));
  } else if (kind === 'noul') {
    criteria = Object.entries(q.criteria).map(([key, anchor]) => ({
      key: key === 'true' ? 'yes' : key === 'false' ? 'no' : key,
      anchor,
    }));
  } else {
    criteria = Object.entries(q.criteria).map(([key, anchor]) => ({ key, anchor }));
  }
  return { name, kind, instructions: q.instructions, criteria };
}

/** Server set → view model (bands' conditions become JSON text; stateCaps a named list). */
export function toUiQuestionSet(s: ApiJevQuestionSet): JevQuestionSet {
  return {
    key: s.setKey as JevSetKey,
    title: s.displayName,
    purpose: s.description ?? '',
    questions: Object.entries(s.questions).map(([name, q]) => toUiQuestion(name, q)),
    bandRules: s.bands.map((b) => ({
      name: b.name,
      isDefault: b.default === true,
      when: b.when ? JSON.stringify(b.when) : '',
    })),
    limits: Object.entries(s.stateCaps).map(([name, value]) => ({ name, value })),
    calibrated: s.calibrated,
    calibratedAt: s.calibrated ? (s.calibrationCheckedAt ?? null) : null,
    updatedAt: s.updatedAt,
    updatedBy: s.updatedByName ?? s.updatedBy ?? '—',
  };
}

/** View set → PUT body (only the editable part: questions, band rules, limits). */
export function toQuestionSetBody(set: JevQuestionSet): JevQuestionSetBody {
  return questionSetToApi(set);
}

/** A 400 JEV_SET_INVALID body → the issues it carries (empty for any other error). */
export function setIssuesOf(error: unknown): JevSetIssue[] {
  const body = errorBodyOf(error) as { error?: { code?: unknown; issues?: unknown } } | undefined;
  if (body?.error?.code !== 'JEV_SET_INVALID' || !Array.isArray(body.error.issues)) return [];
  return body.error.issues.filter(
    (i): i is JevSetIssue =>
      !!i &&
      typeof (i as JevSetIssue).code === 'string' &&
      typeof (i as JevSetIssue).detail === 'string',
  );
}

// ---------------------------------------------------------------------------
// T02 — transport + hooks
// ---------------------------------------------------------------------------

async function fetchQuestionSets(): Promise<JevQuestionSet[]> {
  const result = await apiClient.get<unknown>('/admin/jev/question-sets', { command: COMMAND });
  return jevQuestionSetListResponseSchema.parse(unwrap(result)).sets.map(toUiQuestionSet);
}

async function putQuestionSet(set: JevQuestionSet): Promise<JevQuestionSet> {
  const result = await apiClient.put<unknown>(
    `/admin/jev/question-sets/${encodeURIComponent(set.key)}`,
    toQuestionSetBody(set),
    { command: COMMAND },
  );
  try {
    return toUiQuestionSet(jevQuestionSetSchema.parse(unwrap(result)));
  } catch (err) {
    const issues = setIssuesOf(err);
    if (issues.length === 0) throw err;
    const lines = issues.map((i) => issueToLintError(i, set.questions).message);
    throw new Error(`The set was not saved — ${lines.join(' ')}`);
  }
}

export function useJevQuestionSets(): UseQueryResult<JevQuestionSet[], Error> {
  return useQuery<JevQuestionSet[], Error>({ queryKey: SETS_KEY, queryFn: fetchQuestionSets });
}

/** Save a set. The server re-runs the shape check; a change of questions resets calibration (§5.6). */
export function useSaveJevQuestionSet(): UseMutationResult<JevQuestionSet, Error, JevQuestionSet> {
  const invalidate = useInvalidateJev();
  return useMutation<JevQuestionSet, Error, JevQuestionSet>({
    mutationFn: putQuestionSet,
    retry: false,
    onSuccess: () => void invalidate(),
  });
}

// ---------------------------------------------------------------------------
// T03 — thresholds: mapping (pure)
// ---------------------------------------------------------------------------

const THRESHOLDS_KEY = [...JEV_QUERY_KEY, 'thresholds'] as const;

/** Human labels of the server's named thresholds (unknown names show as themselves). */
export const THRESHOLD_LABEL: Readonly<Record<string, string>> = {
  likelyJunkMinConfidence: 'Probable junk — min. confidence',
  needsHumanGradeConfidence: 'A/B below this grade confidence → needs human',
};

/** Server version source (+ its note) → the history pill. */
export function toUiVersionSource(v: ApiJevThresholdVersion): JevVersionSource {
  if (v.source === 'rollback') return 'rollback';
  if (v.source === 'default') return 'reset';
  if (v.source === 'manual') return 'initial';
  return /^recalibration\b/.test(v.note ?? '') ? 'recalibration' : 'auto';
}

/** The stored note (`auto: 12 …`, `rollback to v3`, `set v2: …`) → one English sentence. */
export function versionReason(v: ApiJevThresholdVersion): string {
  const note = (v.note ?? '').trim();
  const rows = /^(auto|recalibration):\s*(\d+)/.exec(note);
  if (rows) {
    return rows[1] === 'recalibration'
      ? `Recalibration: fitted on ${rows[2]} labelled rows.`
      : `Recalculated automatically on ${rows[2]} labelled rows.`;
  }
  const back = /^rollback to v(\d+)/.exec(note);
  if (back) return `Rolled back to v${back[1]} — auto-recalculation frozen.`;
  if (v.source === 'default') {
    const sv = /^set v(\d+)/.exec(note);
    return sv
      ? `Questions edited (set v${sv[1]}) — calibration reset, safe defaults until recalibration.`
      : 'Safe defaults (not calibrated on labels yet).';
  }
  return note || '—';
}

export function toUiThresholdVersion(v: ApiJevThresholdVersion): JevThresholdVersion {
  return {
    version: v.version,
    createdAt: v.createdAt,
    source: toUiVersionSource(v),
    reason: versionReason(v),
    values: Object.entries(v.thresholds).map(([name, value]) => ({
      name,
      label: THRESHOLD_LABEL[name] ?? name,
      value,
    })),
  };
}

function statInt(stats: Record<string, unknown> | null | undefined, key: string): number {
  const v = stats?.[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

/**
 * One set + its history (newest first) + its last recalibration run → the card's model.
 * Safeguard warnings: the last rejected recompute (`calibrationWarning`) and a failed run.
 */
export function toUiSetThresholds(
  set: ApiJevQuestionSet,
  versions: readonly ApiJevThresholdVersion[],
  run: JevRecalibrationRun | null,
): JevSetThresholds {
  const current = versions.find((v) => v.isCurrent) ?? set.currentThresholds ?? versions[0];
  const warnings: JevSafeguardWarning[] = [];
  if (set.calibrationWarning) {
    warnings.push({
      at: set.calibrationCheckedAt ?? set.updatedAt,
      message: set.calibrationWarning,
    });
  }
  if (run?.status === 'failed') {
    const why = run.error ? `: ${run.error}` : '';
    warnings.push({
      at: run.finishedAt ?? run.startedAt,
      message: `The last recalibration failed after ${run.done} of ${run.total} rows${why}. Thresholds were not changed.`,
    });
  }
  return {
    setKey: set.setKey as JevSetKey,
    currentVersion: current?.version ?? 0,
    frozen: set.thresholdsFrozen,
    positives: statInt(set.calibrationStats, 'positives'),
    labels: statInt(set.calibrationStats, 'samples'),
    versions: versions.map(toUiThresholdVersion),
    warnings,
    recalibration:
      run?.status === 'running'
        ? { startedAt: run.startedAt, rows: run.total, done: run.done }
        : null,
  };
}

/** Recalibration preview → the confirmation dialog's quote (rows = paid calls). */
export function toUiRecalibrationQuote(p: JevRecalibrationPreview): JevRecalibrationQuote {
  return {
    setKey: p.setKey as JevSetKey,
    rows: p.rows,
    costPerRowUsd: p.rows > 0 ? p.estimatedCostUsd / p.rows : 0,
    totalUsd: p.estimatedCostUsd,
    isEstimate: p.priceIsFallbackEstimate,
  };
}

// ---------------------------------------------------------------------------
// T03 — transport + hooks
// ---------------------------------------------------------------------------

const thresholdVersionListSchema = z.object({ versions: z.array(jevThresholdVersionSchema) });

const setPath = (setKey: string): string =>
  `/admin/jev/question-sets/${encodeURIComponent(setKey)}`;

async function fetchVersions(setKey: string): Promise<ApiJevThresholdVersion[]> {
  const result = await apiClient.get<unknown>(`${setPath(setKey)}/thresholds`, {
    command: COMMAND,
  });
  return thresholdVersionListSchema.parse(unwrap(result)).versions;
}

async function fetchLatestRun(setKey: string): Promise<JevRecalibrationRun | null> {
  const result = await apiClient.get<unknown>(`${setPath(setKey)}/recalibration/latest`, {
    command: COMMAND,
  });
  return jevRecalibrationLatestResponseSchema.parse(unwrap(result)).run;
}

async function fetchThresholds(): Promise<JevSetThresholds[]> {
  const result = await apiClient.get<unknown>('/admin/jev/question-sets', { command: COMMAND });
  const sets = jevQuestionSetListResponseSchema.parse(unwrap(result)).sets;
  return Promise.all(
    sets.map(async (s) => {
      const [versions, run] = await Promise.all([
        fetchVersions(s.setKey),
        fetchLatestRun(s.setKey),
      ]);
      return toUiSetThresholds(s, versions, run);
    }),
  );
}

export function useJevThresholds(): UseQueryResult<JevSetThresholds[], Error> {
  return useQuery<JevSetThresholds[], Error>({
    queryKey: THRESHOLDS_KEY,
    queryFn: fetchThresholds,
    // Poll only while a recalibration runs — the progress bar moves, then stops.
    refetchInterval: (q) => (q.state.data?.some((t) => t.recalibration) ? 4000 : false),
  });
}

/** Rollback by the version NUMBER the admin sees; the server wants the version row id. */
async function postRollback(setKey: JevSetKey, version: number): Promise<void> {
  const target = (await fetchVersions(setKey)).find((v) => v.version === version);
  if (!target) throw new Error('That version no longer exists — reload the page.');
  if (target.isCurrent) throw new Error(`v${version} is already the current version.`);
  unwrap(
    await apiClient.post<unknown>(
      `${setPath(setKey)}/thresholds/rollback`,
      { versionId: target.id },
      { command: COMMAND },
    ),
  );
}

/** Roll back to any past version — applies at once and freezes the auto-recalculation. */
export function useRollbackJevThresholds(): UseMutationResult<
  void,
  Error,
  { setKey: JevSetKey; version: number }
> {
  const invalidate = useInvalidateJev();
  return useMutation<void, Error, { setKey: JevSetKey; version: number }>({
    mutationFn: ({ setKey, version }) => postRollback(setKey, version),
    retry: false,
    onSuccess: () => void invalidate(),
  });
}

/** Freeze / resume the free auto-recalculation (resume runs one recalculation at once). */
export function useSetJevFreeze(): UseMutationResult<
  void,
  Error,
  { setKey: JevSetKey; frozen: boolean }
> {
  const invalidate = useInvalidateJev();
  return useMutation<void, Error, { setKey: JevSetKey; frozen: boolean }>({
    mutationFn: async ({ setKey, frozen }) => {
      unwrap(
        await apiClient.put<unknown>(
          `${setPath(setKey)}/thresholds/freeze`,
          { frozen },
          { command: COMMAND },
        ),
      );
    },
    retry: false,
    onSuccess: () => void invalidate(),
  });
}

/** Rows × average price for a recalibration. Read-only, free (no Jev call). */
export function useJevRecalibrationQuote(
  setKey: JevSetKey | null,
): UseQueryResult<JevRecalibrationQuote, Error> {
  return useQuery<JevRecalibrationQuote, Error>({
    queryKey: [...THRESHOLDS_KEY, 'quote', setKey],
    enabled: setKey !== null,
    staleTime: 0,
    queryFn: async () => {
      const result = await apiClient.get<unknown>(
        `${setPath(setKey ?? '')}/recalibration/preview`,
        { command: COMMAND },
      );
      return toUiRecalibrationQuote(jevRecalibrationPreviewSchema.parse(unwrap(result)));
    },
  });
}

/**
 * Start the PAID recalibration. `confirmedRows` is the row count the admin saw in the
 * dialog; the server refuses (409 RECALIBRATION_ROWS_CHANGED) when it no longer matches.
 */
export function useStartJevRecalibration(): UseMutationResult<
  void,
  Error,
  { setKey: JevSetKey; confirmedRows: number }
> {
  const invalidate = useInvalidateJev();
  return useMutation<void, Error, { setKey: JevSetKey; confirmedRows: number }>({
    mutationFn: async ({ setKey, confirmedRows }) => {
      unwrap(
        await apiClient.post<unknown>(
          `${setPath(setKey)}/recalibration`,
          { confirmedRows },
          { command: COMMAND },
        ),
      );
    },
    retry: false,
    onSuccess: () => void invalidate(),
  });
}

// ---------------------------------------------------------------------------
// T04 — metrics + set check: mapping (pure)
// ---------------------------------------------------------------------------

const METRICS_KEY = [...JEV_QUERY_KEY, 'metrics'] as const;
const CHECK_ROWS_KEY = [...JEV_QUERY_KEY, 'check-rows'] as const;
const KNOWN_SET_KEYS: readonly string[] = [
  'research_triage',
  'research_verdict',
  'research_same_company',
];

/** Server metrics → view model (only the sets the admin block knows). */
export function toUiMetrics(r: JevMetricsResponse): JevMetrics {
  return {
    fromDate: r.fromDate,
    toDate: r.toDate,
    sets: r.sets
      .filter((m) => KNOWN_SET_KEYS.includes(m.setKey))
      .map((m) => ({
        setKey: m.setKey as JevSetKey,
        decisions: m.decisions,
        labels: m.labels,
        agreement: m.agreement,
        junkShare: m.junkShare,
        needsHumanShare: m.needsHumanShare,
        costUsd: m.costUsd,
      })),
  };
}

export function toUiCheckRow(r: JevCheckRowApi): JevCheckRow {
  return {
    id: String(r.id),
    requestId: `#${r.requestId}`,
    requestTitle: r.requestTitle,
    company: r.company,
    domain: r.domain || '—',
    city: r.city || '—',
    state: r.state || '—',
    employees: r.employees || '—',
  };
}

/**
 * The server's band for the result pill: triage buckets (`likely_junk` shows as Probable junk,
 * plain `check` has no pill); a verdict shows Needs human only when the grade needs one.
 */
export function toUiCheckBand(
  r: Pick<JevCheckResultApi, 'setKey' | 'band' | 'needsHuman'>,
): JevBand | null {
  if (r.setKey === 'research_verdict') return r.needsHuman ? 'needs_human' : null;
  if (r.setKey !== 'research_triage') return null;
  if (r.band === 'likely_junk') return 'probable_junk';
  if (r.band === 'check_first' || r.band === 'doubtful') return r.band;
  return null;
}

export function toUiCheckResult(r: JevCheckResultApi): JevCheckResult {
  return {
    setKey: r.setKey as JevSetKey,
    rowId: String(r.rowId),
    answers: r.answers.map((a) => ({
      question: a.question,
      kind: a.kind,
      answer: a.answer,
      confidence: a.confidence,
      probabilities: a.probabilities,
    })),
    band: toUiCheckBand(r),
    reason: r.reason,
    costUsd: r.costUsd,
    latencyMs: r.latencyMs,
    stateChars: r.stateChars,
  };
}

// ---------------------------------------------------------------------------
// T04 — transport + hooks
// ---------------------------------------------------------------------------

export function useJevMetrics(): UseQueryResult<JevMetrics, Error> {
  return useQuery<JevMetrics, Error>({
    queryKey: METRICS_KEY,
    queryFn: async () => {
      const result = await apiClient.get<unknown>('/admin/jev/metrics?days=30', {
        command: COMMAND,
      });
      return toUiMetrics(jevMetricsResponseSchema.parse(unwrap(result)));
    },
  });
}

export function useJevCheckRows(): UseQueryResult<JevCheckRow[], Error> {
  return useQuery<JevCheckRow[], Error>({
    queryKey: CHECK_ROWS_KEY,
    queryFn: async () => {
      const result = await apiClient.get<unknown>('/admin/jev/check-rows', { command: COMMAND });
      return jevCheckRowsResponseSchema.parse(unwrap(result)).rows.map(toUiCheckRow);
    },
  });
}

/** The price of ONE check call of the set. Read-only, free (no Jev call). */
export function useJevCheckQuote(setKey: JevSetKey | null): UseQueryResult<JevCheckQuote, Error> {
  return useQuery<JevCheckQuote, Error>({
    queryKey: [...JEV_QUERY_KEY, 'check-quote', setKey],
    enabled: setKey !== null,
    staleTime: 0,
    queryFn: async () => {
      const result = await apiClient.get<unknown>(`${setPath(setKey ?? '')}/check-quote`, {
        command: COMMAND,
      });
      const q = jevCheckQuoteSchema.parse(unwrap(result));
      return { costUsd: q.costUsd, isEstimate: q.isEstimate };
    },
  });
}

/**
 * ONE paid Jev call on one row. Never retried; nothing is invalidated — the check writes nothing
 * (the row, its bucket and grade stay as they are).
 */
export function useRunJevSetCheck(): UseMutationResult<
  JevCheckResult,
  Error,
  { setKey: JevSetKey; rowId: string }
> {
  return useMutation<JevCheckResult, Error, { setKey: JevSetKey; rowId: string }>({
    mutationFn: async ({ setKey, rowId }) => {
      const result = await apiClient.post<unknown>(
        `${setPath(setKey)}/check`,
        { rowId: Number(rowId) },
        { command: COMMAND },
      );
      return toUiCheckResult(jevCheckResultSchema.parse(unwrap(result)));
    },
    retry: false,
  });
}
